import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import config, { SESSION_EXPIRE_WARNING_MINUTES, SESSION_EXPIRE_MINUTES } from '../config';
import { apiAxiosGet, apiAxiosPost, apiAxiosPostLogin } from '../utils/api';

// JWT 토큰 디코딩 함수
const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('JWT 디코딩 실패:', error);
    return null;
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenError, setTokenError] = useState(null);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(null);
  const [showSessionPopup, setShowSessionPopup] = useState(false);
  const [isExtendingSession, setIsExtendingSession] = useState(false);
  const [sessionRefreshKey, setSessionRefreshKey] = useState(0);
  
  // useRef를 사용하여 타이머를 직접 관리 (state 업데이트 지연 문제 해결)
  const sessionTimerRef = useRef(null);
  // 이 탭이 알고 있는 만료 시각(ms). 연장·로그인할 때와 다른 탭이 토큰을 바꿨을 때만 고친다
  const expiryRef = useRef(null);
  // 만료 경고를 이미 띄운 만료 시각
  const warnedExpiryRef = useRef(null);

  useEffect(() => {
    // 토큰이 저장되어 있는지 확인
    const token = localStorage.getItem('token');
    
    if (token) {
      // 토큰 유효성 검증
      validateToken(token);
    } else {
      setLoading(false);
    }
    // localStorage 토큰 변경 모니터링 (다른 탭에서의 변경 감지)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        // 다른 탭에서 토큰이 변경된 경우 재검증
        if (e.newValue) {
          // 다른 탭·창에서 연장한 토큰이면 이 탭의 만료 시각도 따라간다
          const expiry = getTokenExpiry(e.newValue);
          if (expiry && sessionTimerRef.current) expiryRef.current = expiry.getTime();
          validateToken(e.newValue);
        } else {
          handleSessionExpired();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      clearSessionTimer();
    };
  }, []);


  // JWT 토큰에서 만료 시간 가져오기
  const getTokenExpiry = (token) => {
    if (!token) return null;
    
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) return null;
    
    // exp는 Unix timestamp (초 단위)이므로 밀리초로 변환
    return new Date(decoded.exp * 1000);
  };

  const validateToken = async (token) => {
    try {
      // 먼저 JWT 토큰에서 만료 시간 확인
      const tokenExpiry = getTokenExpiry(token);
      
      if (tokenExpiry && tokenExpiry <= new Date()) {
        handleSessionExpired();
        return;
      }

      // 토큰이 유효한 경우에만 서버 검증 시도
      if (!tokenExpiry || tokenExpiry > new Date()) {
        try {
          const response = await apiAxiosGet('/api/auth/validate');
          
          if (response.valid) {
            setIsAuthenticated(true);
            setUser(response.user);
            setTokenError(null);
            // 세션 타이머 시작 (기존 타이머가 없는 경우에만)
            if (!sessionTimerRef.current) {
              const expiresAt = tokenExpiry || new Date(Date.now() + SESSION_EXPIRE_MINUTES * 60 * 1000);
              startSessionTimer(expiresAt.toISOString());
            }
          } else {
            handleSessionExpired();
          }
        } catch (serverError) {
          // 서버 오류인 경우 JWT 토큰이 유효하면 인증 상태 유지
          if (tokenExpiry && tokenExpiry > new Date()) {
            setIsAuthenticated(true);
            setUser({ id: '사용자' });
            setTokenError('서버 연결에 문제가 있습니다. 잠시 후 다시 시도해주세요.');
            // 기존 타이머가 없는 경우에만 새 타이머 시작
            if (!sessionTimerRef.current) {
              startSessionTimer(tokenExpiry.toISOString());
            }
          } else {
            handleSessionExpired();
          }
        }
      } else {
        handleSessionExpired();
      }
    } catch (error) {
      // 예상치 못한 오류인 경우에도 JWT 토큰 확인
      const tokenExpiry = getTokenExpiry(token);
      if (tokenExpiry && tokenExpiry > new Date()) {
        setIsAuthenticated(true);
        setUser({ id: '사용자' });
        setTokenError('연결에 문제가 있습니다. 잠시 후 다시 시도해주세요.');
        if (!sessionTimerRef.current) {
          startSessionTimer(tokenExpiry.toISOString());
        }
      } else {
        handleSessionExpired();
      }
    } finally {
      setLoading(false);
    }
  };

  const retryTokenValidation = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      setLoading(true);
      setTokenError(null);
      await validateToken(token);
    }
  };

  const startSessionTimer = (expiresAt) => {
    // 기존 타이머가 있으면 먼저 정리
    clearSessionTimer();
    
    let expiryDate;
    // JWT 토큰의 실제 만료시간을 사용하거나, 기본값 사용
    if (expiresAt) {
      expiryDate = new Date(expiresAt);
    } else {
      expiryDate = new Date(Date.now() + SESSION_EXPIRE_MINUTES * 60 * 1000);
    }
    
    expiryRef.current = expiryDate.getTime();

    const updateTimer = () => {
      const now = new Date().getTime();
      let expiryTime = expiryRef.current;
      let timeLeft = Math.max(0, expiryTime - now);

      if (timeLeft <= 0) {
        // 로그아웃하기 직전에 한 번만 저장된 토큰을 다시 본다. 다른 탭에서 연장했는데 알림을 놓쳤다면
        // (연장 전 시각에 로그아웃하면 토큰이 지워져 연장한 탭까지 함께 로그아웃된다) 그 시각으로 이어 간다
        const stored = getTokenExpiry(localStorage.getItem('token'));
        if (stored && stored.getTime() > now) {
          expiryRef.current = stored.getTime();
          expiryTime = expiryRef.current;
          timeLeft = expiryTime - now;
        }
      }
      if (timeLeft <= 0) {
        handleSessionExpired();
        return;
      }
      
      setSessionTimeLeft(timeLeft);
      
      const warningMinutes = SESSION_EXPIRE_WARNING_MINUTES;
      if (timeLeft > warningMinutes * 60 * 1000) {
        // 다른 탭에서 연장되어 여유가 생겼으면 떠 있던 경고를 내린다
        if (warnedExpiryRef.current !== null && warnedExpiryRef.current !== expiryTime) {
          warnedExpiryRef.current = null;
          setShowSessionPopup(false);
        }
      } else if (warnedExpiryRef.current !== expiryTime) {
        // 같은 만료 시각에 대해서는 한 번만 띄운다(닫아도 매초 다시 뜨지 않게)
        warnedExpiryRef.current = expiryTime;
        setShowSessionPopup(true);
      }
    };
    
    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    // useRef를 사용하여 타이머 저장 (즉시 반영)
    sessionTimerRef.current = timer;
  };

  const clearSessionTimer = () => {
    // useRef를 사용하여 타이머 직접 참조 및 정리
    if (sessionTimerRef.current) {
      clearInterval(sessionTimerRef.current);
      sessionTimerRef.current = null;
    }
    setSessionTimeLeft(null);
    setShowSessionPopup(false);
  };

  const clearAllSessionData = () => {
    clearSessionTimer();
    setSessionTimeLeft(null);
    setShowSessionPopup(false);
    setIsExtendingSession(false);
    setSessionRefreshKey(0);
  };

  const handleSessionExpired = () => {
    clearAllSessionData();
    setIsAuthenticated(false);
    setUser(null);
    setTokenError('세션이 만료되었습니다. 다시 로그인해주세요.');
    localStorage.removeItem('token');
  };

  const extendSession = async () => {
    if (isExtendingSession) {
      return { success: false, error: '이미 세션 연장 중입니다.' };
    }

    const token = localStorage.getItem('token');
    
    try {
      setIsExtendingSession(true);
      
      if (!token) {
        return { success: false, error: '토큰이 없습니다. 다시 로그인해주세요.' };
      }

      const response = await apiAxiosPost('/api/auth/refresh', {});

      if (response.access_token) {
        const newToken = response.access_token;
        localStorage.setItem('token', newToken);
        
        // 기존 세션 데이터 정리
        clearAllSessionData();
        
        // 새로운 토큰으로 사용자 정보 설정
        setIsAuthenticated(true);
        setUser(response.user);
        setTokenError(null);
        
        // 새로운 토큰의 만료 시간으로 타이머 시작
        const tokenExpiry = getTokenExpiry(newToken);
        if (tokenExpiry) {
          setSessionRefreshKey(prev => prev + 1);
          startSessionTimer(tokenExpiry.toISOString());
        } else {
          setSessionRefreshKey(prev => prev + 1);
          startSessionTimer(new Date(Date.now() + SESSION_EXPIRE_MINUTES * 60 * 1000).toISOString());
        }
        
        return { success: true };
      } else {
        return { success: false, error: '토큰 갱신에 실패했습니다.' };
      }
    } catch (error) {
      if (error.response?.status === 401) {
        handleSessionExpired();
        return { success: false, error: '토큰이 만료되었습니다. 다시 로그인해주세요.' };
      }
      
      return { success: false, error: '세션 연장에 실패했습니다.' };
    } finally {
      setIsExtendingSession(false);
    }
  };

  const closeSessionPopup = () => {
    setShowSessionPopup(false);
  };

  const login = async (id, password) => {
    try {
      clearAllSessionData();
      
      const response = await apiAxiosPostLogin('/api/auth/login', {
        id,
        password
      });
      
      const { access_token, user } = response;
      
      localStorage.setItem('token', access_token);
      
      setIsAuthenticated(true);
      setUser(user);
      setTokenError(null);
      
      const tokenExpiry = getTokenExpiry(access_token);
      
      if (tokenExpiry) {
        startSessionTimer(tokenExpiry.toISOString());
      } else {
        startSessionTimer(new Date(Date.now() + SESSION_EXPIRE_MINUTES * 60 * 1000).toISOString());
      }
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || '로그인에 실패했습니다.' 
      };
    }
  };

  const logout = () => {
    clearAllSessionData();
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
    setTokenError(null);
  };

  // 사용자 정보 일부를 갱신 (예: 담당자 색상 변경 후 즉시 반영)
  const updateUser = (partial) => {
    setUser(prev => (prev ? { ...prev, ...partial } : prev));
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    tokenError,
    sessionTimeLeft,
    showSessionPopup,
    isExtendingSession,
    sessionRefreshKey,
    retryTokenValidation,
    login,
    logout,
    updateUser,
    extendSession,
    closeSessionPopup
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

